import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchWithCorpId } from '../utils/api';

const POLL_INTERVAL_MS = 60000;

const timeAgo = (isoDateTime) => {
  if (!isoDateTime) return '';
  const then = new Date(isoDateTime);
  const seconds = Math.floor((Date.now() - then.getTime()) / 1000);
  if (seconds < 60) return 'Just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
  return then.toLocaleDateString();
};

// Where each notification navigates. Timesheet notifications open the
// timesheet's items; expenses and invoices open their list screens; document
// uploads and document requests open the Documents page (uploads sort newest
// first there, and the requirements checklist lives on the same page).
// entityType casing varies across backend writers ("TIMESHEET" vs
// "document"), so the comparison is case-insensitive (#120).
const targetPath = (notification) => {
  const type = (notification.entityType || '').toUpperCase();
  if (type === 'TIMESHEET' && notification.entityId) {
    return `/on/timesheet-items?timesheetId=${notification.entityId}`;
  }
  if (type === 'EXPENSE') return '/on/expenses';
  if (type === 'INVOICE') return '/on/invoices';
  if (type === 'DOCUMENT' || type === 'DOCUMENT_REQUIREMENT') return '/on/documents';
  return null;
};

function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();

  const refreshCount = useCallback(async () => {
    try {
      const data = await fetchWithCorpId('/notifications/unread-count', { silent: true });
      setUnreadCount(data.count || 0);
    } catch (e) { /* polling failure is not worth surfacing */ }
  }, []);

  useEffect(() => {
    refreshCount();
    const timer = setInterval(refreshCount, POLL_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refreshCount]);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClickOutside);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const openPanel = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const rows = await fetchWithCorpId('/notifications', { silent: true });
      setNotifications(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  const handleItemClick = async (notification) => {
    if (!notification.readAt) {
      try {
        const data = await fetchWithCorpId(`/notifications/${notification.id}/read`, {
          method: 'PUT',
          silent: true,
        });
        setUnreadCount(data.count || 0);
        setNotifications(items => items.map(n =>
          n.id === notification.id ? { ...n, readAt: new Date().toISOString() } : n));
      } catch (e) { /* navigation still works if marking read fails */ }
    }
    const path = targetPath(notification);
    if (path) {
      setOpen(false);
      navigate(path);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      const data = await fetchWithCorpId('/notifications/read-all', {
        method: 'PUT',
        silent: true,
      });
      setUnreadCount(data.count || 0);
      const now = new Date().toISOString();
      setNotifications(items => items.map(n => (n.readAt ? n : { ...n, readAt: now })));
    } catch (e) { /* ignore */ }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        onClick={() => (open ? setOpen(false) : openPanel())}
        title="Notifications"
        aria-label="Notifications"
        style={{
          position: 'relative',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          width: '40px', height: '40px', borderRadius: 'var(--radius-md)',
          background: 'transparent', border: '1px solid var(--hairline)',
          color: 'var(--ink)', cursor: 'pointer',
          transition: 'background 0.16s, border-color 0.16s',
        }}
        onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-soft)'; e.currentTarget.style.borderColor = 'var(--surface-cream-strong)'; }}
        onMouseOut={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'var(--hairline)'; }}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute', top: '-6px', right: '-6px',
            minWidth: '18px', height: '18px', padding: '0 5px',
            borderRadius: '9px', background: 'var(--coral)',
            color: 'var(--on-primary)',
            fontFamily: 'var(--font-sans)', fontSize: '11px', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            lineHeight: 1,
          }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '48px', right: 0,
          width: '360px', maxHeight: '440px',
          display: 'flex', flexDirection: 'column',
          background: 'var(--canvas)',
          border: '1px solid var(--hairline)',
          borderRadius: 'var(--radius-md)',
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          zIndex: 200,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px', borderBottom: '1px solid var(--hairline-soft)',
          }}>
            <span style={{ fontFamily: 'var(--font-sans)', fontSize: '13px', fontWeight: 600, color: 'var(--ink)' }}>
              Notifications
            </span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer', padding: 0,
                  fontFamily: 'var(--font-sans)', fontSize: '12px', fontWeight: 500,
                  color: 'var(--brand-blue)',
                }}
              >
                Mark all read
              </button>
            )}
          </div>

          <div style={{ overflowY: 'auto' }}>
            {loading ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--muted)' }}>
                Loading...
              </div>
            ) : notifications.length === 0 ? (
              <div style={{ padding: '24px 16px', textAlign: 'center', fontFamily: 'var(--font-sans)', fontSize: '13px', color: 'var(--muted)' }}>
                No notifications yet.
              </div>
            ) : notifications.map(n => (
              <button
                key={n.id}
                onClick={() => handleItemClick(n)}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: '10px',
                  width: '100%', padding: '12px 16px', textAlign: 'left',
                  background: n.readAt ? 'transparent' : 'var(--surface-soft)',
                  border: 'none', borderBottom: '1px solid var(--hairline-soft)',
                  cursor: 'pointer',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--surface-cream-strong)'; }}
                onMouseOut={e => { e.currentTarget.style.background = n.readAt ? 'transparent' : 'var(--surface-soft)'; }}
              >
                <span style={{
                  width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0, marginTop: '5px',
                  background: n.readAt ? 'transparent' : 'var(--coral)',
                }} />
                <span style={{ flex: 1 }}>
                  <span style={{
                    display: 'block',
                    fontFamily: 'var(--font-sans)', fontSize: '13px',
                    fontWeight: n.readAt ? 400 : 500,
                    color: 'var(--ink)', lineHeight: 1.4,
                  }}>
                    {n.message}
                  </span>
                  <span style={{
                    display: 'block', marginTop: '2px',
                    fontFamily: 'var(--font-sans)', fontSize: '11px', color: 'var(--muted)',
                  }}>
                    {timeAgo(n.createdAt)}
                  </span>
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
